import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { Slider, Text } from "@wojo/ui";
import { useId } from "react";
import {
    useTourPageContext,
    WeatherContextProvider,
} from "../../_client/context";
import styles from "./Weather.module.scss";
import { WeatherCard } from "./WeatherCard";
import { TourSectionId } from "../../_client/hooks";
import { City, PlaceType, Place, Country } from "../../../generated/graphql";

const isCity = (place: Place): place is City => {
    return place.type === PlaceType.City;
};
const isCountry = (place: Place): place is Country => {
    return place.type === PlaceType.Country;
};

export const Weather: React.FC = () => {
    const { productPage } = useI18n("productPage");
    const headingId = useId();
    const { tourContent } = useTourPageContext();
    const cities = tourContent.tour?.places.filter(isCity);

    if (!cities?.length) {
        return null;
    }

    const countries = tourContent.tour?.places.filter(isCountry);
    const countryMap = countries
        ? Object.fromEntries(
              countries.map((country) => [country._id, country.name]),
          )
        : {};

    const monthlyWeatherPerCity = cities.map((city) => {
        const cityWeather = [...city.monthlyWeathers];
        cityWeather.sort((a, b) => a.month - b.month);
        const country = countryMap[city.parentIds[0]];
        const monthlyWeather = {
            avgMinTempsC: new Array<number>(),
            avgMinTempsF: new Array<number>(),
            avgMaxTempsC: new Array<number>(),
            avgMaxTempsF: new Array<number>(),
            avgMonthlyRainfallCm: new Array<number>(),
            avgMonthlyRainfallInch: new Array<number>(),
        };
        cityWeather.forEach((weather) => {
            monthlyWeather.avgMinTempsC.push(weather.averageLowCelsius);
            monthlyWeather.avgMinTempsF.push(weather.averageLowFahrenheit);
            monthlyWeather.avgMaxTempsC.push(weather.averageHighCelsius);
            monthlyWeather.avgMaxTempsF.push(weather.averageHighFahrenheit);
            monthlyWeather.avgMonthlyRainfallCm.push(
                weather.averageRainCentimeters,
            );
            monthlyWeather.avgMonthlyRainfallInch.push(
                weather.averageRainInches,
            );
        });
        return {
            id: city._id,
            place: country ? `${city.name}, ${country}` : city.name,
            monthlyWeather,
        };
    });

    return (
        <section
            aria-labelledby={headingId}
            className={styles.section}
            id={TourSectionId.WEATHER}
            tabIndex={-1}
        >
            <Text.Heading
                order="3"
                tag="h2"
                id={headingId}
                style={{ marginBottom: "var(--g-spacing-lg)" }}
            >
                {productPage.weather.heading.l()}
            </Text.Heading>
            <WeatherContextProvider>
                <Slider
                    desktopWidth="528px"
                    tabletWidth="528px"
                    mobileWidth="320px"
                    slidesToScroll="auto"
                    onSliderButtonClick={(direction) => {
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_action: "SCROLLED",
                                ga4_object: "WEATHER",
                                direction,
                            },
                        });
                    }}
                >
                    {monthlyWeatherPerCity.map((cityMonthlyWeather) => {
                        return (
                            <WeatherCard
                                key={cityMonthlyWeather.id}
                                monthlyWeather={
                                    cityMonthlyWeather.monthlyWeather
                                }
                                place={cityMonthlyWeather.place}
                            />
                        );
                    })}
                </Slider>
            </WeatherContextProvider>
        </section>
    );
};
